// Utility functions for calculating essay topic metrics and enrichment

// Calculate uniqueness score (1-5) based on topic differentiation
export const calculateUniquenessScore = (topic, assessmentData) => {
  let score = 3; // Base score
  
  // Check for power story flag
  if (topic.isPowerStory) score += 1;
  
  // Check for exclusive experiences
  const exclusiveKeywords = ['congressional', 'senate', 'representative', 'political', 'campaign', 'funding', 'restructure', 'coalition'];
  const topicText = `${topic.title} ${topic.description}`.toLowerCase();
  const hasExclusiveExperience = exclusiveKeywords.some(keyword => topicText.includes(keyword));
  if (hasExclusiveExperience) score += 1;
  
  // Check for philosophical/abstract thinking
  const philosophicalKeywords = ['distance', 'bridge', 'translation', 'perspective', 'nuance', 'connection'];
  const hasPhilosophicalAngle = philosophicalKeywords.some(keyword => topicText.includes(keyword));
  if (hasPhilosophicalAngle) score += 0.5;
  
  // Check for concrete achievements
  const achievementKeywords = ['victory', 'won', 'achieved', 'succeeded', 'implemented', 'created', 'built'];
  const hasConcreteAchievement = achievementKeywords.some(keyword => topicText.includes(keyword));
  if (hasConcreteAchievement) score += 0.5;
  
  return Math.min(5, Math.round(score));
};

// Analyze evidence strength based on concrete vs abstract language
export const analyzeEvidenceStrength = (topic) => {
  const topicText = `${topic.title} ${topic.description} ${topic.storyContext || ''}`.toLowerCase();
  
  // Strong evidence indicators
  const concreteIndicators = [
    'restructure', 'funding', 'coalition', 'implemented', 'built', 'created',
    'measured', 'increased', 'decreased', 'saved', 'raised', 'organized',
    'launched', 'developed', 'achieved', 'completed', 'won', 'earned'
  ];
  
  // Moderate evidence indicators  
  const moderateIndicators = [
    'helped', 'supported', 'contributed', 'participated', 'assisted',
    'collaborated', 'worked', 'joined', 'engaged', 'involved'
  ];
  
  // Abstract indicators (limited evidence)
  const abstractIndicators = [
    'realized', 'understood', 'learned', 'discovered', 'felt',
    'thought', 'believed', 'considered', 'reflected', 'observed'
  ];
  
  const concreteCount = concreteIndicators.filter(word => topicText.includes(word)).length;
  const moderateCount = moderateIndicators.filter(word => topicText.includes(word)).length;
  const abstractCount = abstractIndicators.filter(word => topicText.includes(word)).length;
  
  // Calculate evidence level
  if (concreteCount >= 3) return { level: 'concrete', score: 5 };
  if (concreteCount >= 1 || moderateCount >= 2) return { level: 'moderate', score: 3 };
  return { level: 'limited', score: 2 };
};

// Estimate writing difficulty (1-5 scale)
export const estimateWritingDifficulty = (topic) => {
  let difficulty = 2; // Base difficulty
  
  const topicText = `${topic.title} ${topic.description}`.toLowerCase();
  
  // Factors that increase difficulty
  if (topicText.includes('philosophical') || topicText.includes('abstract')) difficulty += 2;
  if (topicText.includes('nuance') || topicText.includes('complex')) difficulty += 1;
  if (topicText.includes('political') || topicText.includes('congressional')) difficulty += 1.5;
  if (topicText.includes('bridge') || topicText.includes('translation')) difficulty += 1;
  
  // Factors that decrease difficulty
  if (topicText.includes('concrete') || topicText.includes('specific')) difficulty -= 0.5;
  if (topicText.includes('victory') || topicText.includes('achievement')) difficulty -= 0.5;
  if (topic.isPowerStory) difficulty -= 1; // Recommended topics are easier to write
  
  return Math.max(1, Math.min(5, Math.round(difficulty)));
};

// Estimate time investment in hours
export const estimateTimeInvestment = (topic) => {
  const difficulty = estimateWritingDifficulty(topic);
  
  // Base time estimates by difficulty
  const timeMap = {
    1: '1-2 hours',
    2: '2-3 hours',
    3: '3-4 hours',
    4: '4-5 hours',
    5: '5-6 hours'
  };
  
  return timeMap[difficulty] || '3-4 hours';
};

// Identify potential risk factors
export const identifyRiskFactors = (topic) => {
  const risks = [];
  const topicText = `${topic.title} ${topic.description}`.toLowerCase();
  
  if (topicText.includes('congressional') || topicText.includes('political')) {
    risks.push('May sound elitist if not carefully positioned');
  }
  
  if (topicText.includes('philosophical') || topicText.includes('abstract')) {
    risks.push('Requires deep reflection and careful articulation');
  }
  
  if (topicText.includes('distance') || topicText.includes('bridge')) {
    risks.push('Needs concrete examples to ground abstract concepts');
  }
  
  if (!topic.storyContext) {
    risks.push('May need additional story development');
  }
  
  return risks;
};

// Generate competitive advantage statement
export const generateCompetitiveAdvantage = (topic) => {
  if (topic.differentiator) return topic.differentiator;
  
  const topicText = `${topic.title} ${topic.description}`.toLowerCase();
  
  if (topicText.includes('funding') || topicText.includes('restructure')) {
    return 'Shows rare combination of strategic thinking, tactical execution, and moral courage that most students cannot demonstrate';
  }
  
  if (topicText.includes('distance') || topicText.includes('bridge')) {
    return 'Demonstrates sophisticated understanding of human connection and leadership through translation rather than command';
  }
  
  if (topicText.includes('congressional') || topicText.includes('political')) {
    return 'Provides insider perspective and political sophistication that 99% of applicants cannot match';
  }
  
  return 'Offers unique perspective and concrete evidence of impact';
};

// Generate topic badges
export const generateBadges = (topic) => {
  const badges = [];
  
  if (topic.isPowerStory) {
    badges.push({ type: 'primary', label: 'Your Best Story', color: 'violet' });
  }
  
  const evidence = analyzeEvidenceStrength(topic);
  if (evidence.level === 'concrete') {
    badges.push({ type: 'strength', label: 'Strongest Evidence', color: 'emerald' });
  } else if (evidence.level === 'limited') {
    badges.push({ type: 'philosophical', label: 'Most Philosophical', color: 'azure' });
  }
  
  const uniqueness = calculateUniquenessScore(topic);
  if (uniqueness === 5 && !topic.isPowerStory) {
    badges.push({ type: 'exclusive', label: 'Most Exclusive', color: 'amber' });
  }
  
  return badges;
};

// Main enrichment function
export const enrichTopicWithMetrics = (topic, assessmentData = {}) => {
  const uniqueness = calculateUniquenessScore(topic, assessmentData);
  const evidence = analyzeEvidenceStrength(topic);
  const difficulty = estimateWritingDifficulty(topic);
  const timeInvestment = estimateTimeInvestment(topic);
  const riskFactors = identifyRiskFactors(topic);
  const competitiveAdvantage = generateCompetitiveAdvantage(topic);
  const badges = generateBadges(topic);
  
  return {
    ...topic,
    metrics: {
      uniqueness,
      evidenceStrength: evidence.level,
      evidenceScore: evidence.score,
      writingDifficulty: difficulty,
      timeInvestment
    },
    badges,
    riskFactors,
    competitiveAdvantage,
    recommended: topic.isPowerStory || false
  };
};

// Batch enrichment for multiple topics
export const enrichAllTopics = (topics, assessmentData) => {
  return topics.map(topic => enrichTopicWithMetrics(topic, assessmentData));
};